import React, { createContext, useContext, useState, useEffect } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (password: string) => boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

// You can change this password or make it configurable
const DASHBOARD_PASSWORD = 'AskEngine2025!';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if user was previously authenticated
    const savedAuth = localStorage.getItem('ask-engine-auth');
    if (savedAuth) {
      const authData = JSON.parse(savedAuth);
      // Check if authentication is still valid (expires after 24 hours)
      const now = new Date().getTime();
      const expirationTime = 24 * 60 * 60 * 1000; // 24 hours
      return now - authData.timestamp < expirationTime;
    }
    return false;
  });

  const login = (password: string): boolean => {
    if (password === DASHBOARD_PASSWORD) {
      setIsAuthenticated(true);
      // Save authentication state with timestamp
      localStorage.setItem('ask-engine-auth', JSON.stringify({
        authenticated: true,
        timestamp: new Date().getTime()
      }));
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('ask-engine-auth');
  };

  // Auto-logout after 24 hours
  useEffect(() => {
    if (isAuthenticated) {
      const savedAuth = localStorage.getItem('ask-engine-auth');
      if (savedAuth) {
        const authData = JSON.parse(savedAuth);
        const now = new Date().getTime();
        const expirationTime = 24 * 60 * 60 * 1000; // 24 hours
        
        if (now - authData.timestamp >= expirationTime) {
          logout();
        }
      }
    }
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};